import { Fragment, h } from 'preact'
import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/hooks'
import style from './search-select.css'

export type Item = { [key: string]: any }

type FieldProps = {
    id?: string
    required?: boolean
    items: Item[]
    value?: string
    keyProperty: string
    displayProperty: string
    placeholder?: string
    searchPlaceholder?: string
    label?: string
    helperText?: string
    error?: boolean | string
    errorMessage?: string
    disabled?: boolean
    search?: boolean
    onChange: (value: string) => void
    classes?: {
        root?: string
        button?: string
    }
}
type Props = FieldProps

const CustomMultiselectField = ({
    items = [],
    keyProperty,
    displayProperty,
    placeholder,
    searchPlaceholder,
    label,
    helperText,
    error,
    errorMessage,
    disabled,
    search,
    onChange,
    classes,
    value,
}: Props) => {
    const [isDropdownToggled, setDropdownToggled] = useState<boolean>(false)
    const [searchKeyword, setSearchKeyword] = useState<string>('')

    const dropdownRef = useRef<HTMLDivElement>()

    const closeDropdownOnClick = () => {
        setDropdownToggled(false)
    }

    const closeOnClickOutOfDropdownList = useCallback(
        (event: MouseEvent) => {
            const eventTarget = event.target as HTMLElement
            const isInsideDropdownList =
                dropdownRef.current?.contains(eventTarget) || dropdownRef.current === eventTarget
            const isOnRadioButtonOption = eventTarget.classList.contains('radio-button-option')

            if (!isInsideDropdownList && !isOnRadioButtonOption) {
                event.preventDefault()
                setDropdownToggled(false)
            }
        },
        [setDropdownToggled]
    )

    useEffect(() => {
        window.addEventListener('click', closeOnClickOutOfDropdownList)

        return () => {
            window.removeEventListener('click', closeOnClickOutOfDropdownList)
        }
    }, [closeOnClickOutOfDropdownList])

    const preventPropagation = (event: any) => {
        event.stopPropagation()
        event.nativeEvent.stopImmediatePropagation()
    }

    const toggleDropdown = (event: MouseEvent) => {
        setDropdownToggled(!isDropdownToggled)
    }

    const updateSelectedElements = (newItemsKey: string) => {
        onChange(newItemsKey)
    }

    const handleChangeSearch = ({ target }: { target: EventTarget | null }) => {
        const { value } = target as HTMLInputElement
        setSearchKeyword(value ?? '')
    }

    const handleSearchClear = () => {
        setSearchKeyword('')
    }

    const filteredItems = useMemo(() => {
        if (!searchKeyword) {
            return items
        }
        return items.filter((item) =>
            (item[displayProperty] as string).toLowerCase().includes(searchKeyword.toLowerCase())
        )
    }, [items, searchKeyword, displayProperty])

    const handleSelectSingleOption = (item: Item) => () => {
        updateSelectedElements(item[keyProperty])
        closeDropdownOnClick()
    }

    const renderOption = (item: Item) => {
        const isChecked = value === item[keyProperty]

        return (
            <label
                className={`${style.searchSelectDropdownOptionLabel} ${
                    isChecked ? style.searchSelectDropdownOptionLabelChecked : ''
                }`}
                onClick={handleSelectSingleOption(item)}
            >
                {item[displayProperty]}
            </label>
        )
    }

    const renderDropdown = () => {
        const valueLabel = value
            ? items.find((item: Item) => item[keyProperty] === value)?.[displayProperty]
            : undefined

        return (
            <Fragment>
                <button className={style.searchSelectDropdownButton} onClick={toggleDropdown} disabled={disabled}>
                    {valueLabel ? (
                        <span className={style.searchSelectDropdownText}>{valueLabel}</span>
                    ) : (
                        <span className={style.searchSelectDropdownPlaceholder}>{placeholder}</span>
                    )}
                    <i className={style.searchSelectDropdownArrow}>▼</i>
                </button>

                {isDropdownToggled && (
                    <ul className={style.searchSelectDropdown} onClick={preventPropagation}>
                        {search && (
                            <div className={style.searchSelectSearch}>
                                <span>🔎</span>
                                <input
                                    className={style.searchSelectSearchField}
                                    placeholder={searchPlaceholder || 'Search'}
                                    value={searchKeyword || ''}
                                    onChange={handleChangeSearch}
                                />
                                {searchKeyword && (
                                    <span className={style.searchSelectSearchClear} onClick={handleSearchClear}>
                                        <span>×</span>
                                    </span>
                                )}
                            </div>
                        )}
                        {filteredItems.map((item) => {
                            return (
                                <li className={style.searchSelectDropdownItem} key={item[keyProperty]}>
                                    {renderOption(item)}
                                </li>
                            )
                        })}
                    </ul>
                )}
            </Fragment>
        )
    }

    return (
        <div className={style.searchSelectContainer} ref={dropdownRef}>
            <div
                className={`
                    ${style.searchSelect}
                `}
            >
                {renderDropdown()}
            </div>
        </div>
    )
}

export default CustomMultiselectField
